import { SkyText } from "@skypointcloud/skypoint-ui";
import { QueryClientContext } from "@tanstack/react-query";
import { useCallback, useContext, useSyncExternalStore } from "react";
import type { MeResponse } from "@shared/types";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { ME_QUERY_KEY } from "@/hooks/use-me";
import { serverCommitOf, useServerBuild } from "@/hooks/use-server-build";
import { BUILD_STAMP, type BuildStamp } from "@/lib/env";
import { formatInstant } from "@/lib/time/displayTime";

export type { BuildStamp };

/** The short form a commit is shown in, as `git log --oneline` and the image tag write it. */
const SHORT = 7;

/**
 * #968 (owner 2026-10-14, show less): who may see the commit and the build time. Read from the
 * `/me` the shell already fetched, never fetched here, so a stamp rendered without a signed-in
 * shell shows the short form.
 */
function useIsPlatformAdmin(): boolean {
  const client = useContext(QueryClientContext);
  const subscribe = useCallback(
    (onChange: () => void) => (client ? client.getQueryCache().subscribe(onChange) : () => {}),
    [client],
  );
  const read = () => client?.getQueryData<MeResponse>(ME_QUERY_KEY)?.platformPermissions;
  const permissions = useSyncExternalStore(subscribe, read, read);
  return permissions?.includes("platform.instances.manage") === true;
}

/** `2026.10.14.1-5de881e` as `2026.10.14.1`: the build number without its commit. */
function buildNumber(version: string): string {
  return version.replace(/-[0-9a-f]{7}$/, "");
}

/** Two commits are the same build when one is a prefix of the other (short against full). */
function sameCommit(a: string, b: string): boolean {
  return a.startsWith(b) || b.startsWith(a);
}

function commitComparison(
  ours: string | null,
  theirs: string | null,
): "same" | "different" | "unknown" {
  if (!ours || !theirs) return "unknown";
  return sameCommit(ours, theirs) ? "same" : "different";
}

/**
 * The footer's build stamp (owner, 2026-09-28: "we have no way to identify if we are running on
 * latest version"). The build's version, which carries its date and short commit since #873,
 * "Version 2026.10.12.1" for every role (the commit is not in the label). A local build reads
 * "Version dev". A PlatformAdmin's title holds the build time and the full commit.
 *
 * The bundle was stamped when the image was built; the server answers its own stamp on
 * `/api/health`. When the two differ, a release went out under this open tab, and the stamp
 * says "· server <short sha>" in the warning colour until the page is reloaded.
 *
 * Caption type and muted colour, as the design system's `.app-footer` sets them. `testId`
 * defaults to the footer's `build-version`; a second copy on screen (the user menu's) passes
 * its own, so a query for the footer's stamp stays unique.
 */
export function BuildVersion({
  stamp = BUILD_STAMP,
  testId = "build-version",
  showDetail = false,
}: {
  stamp?: BuildStamp;
  testId?: string;
  /** The user menu's copy: a PlatformAdmin's build time and commit as visible text. */
  showDetail?: boolean;
}) {
  const { data } = useServerBuild();
  const zone = useDisplayTz();
  const admin = useIsPlatformAdmin();
  const ours = stamp.commit?.toLowerCase() ?? null;
  const theirs = serverCommitOf(data);
  const comparison = commitComparison(ours, theirs);

  // Everyone sees the build number, 2026.10.14.2, as the label (owner 2026-10-14, show less;
  // QA retest 2026-10-15: the commit stays out of the label for a PlatformAdmin too). Only a
  // PlatformAdmin also gets the build time, in their display zone and named (never UTC), and the
  // short commit: on hover in the footer, as visible text in the user menu.
  const short = ours?.slice(0, SHORT);
  const version = stamp.version === null ? "dev" : buildNumber(stamp.version);
  const label = `Version ${version}`;
  let title = stamp.version === null ? "Development build" : `Build ${buildNumber(stamp.version)}`;
  let detail: string | null = null;
  if (admin) {
    // "Development build" is only for a bundle with no version. A stamped release whose build
    // time did not reach the bundle says so, never that it is a development build.
    const built = stamp.time
      ? `Built ${formatInstant(stamp.time, zone, "minutes", { named: true })}`
      : stamp.version === null
        ? "Development build"
        : "Build time not recorded";
    detail = short ? `${built}, commit ${short}` : built;
    title = ours ? `${built}, commit ${ours}` : built;
  }
  if (comparison === "different" && theirs) {
    title += admin
      ? `. The server runs commit ${theirs}. Reload the page to load it.`
      : ". A different version is running. Reload the page to load it.";
  }

  return (
    <span data-testid={testId} data-server-build={comparison} title={title}>
      <SkyText size={200} color="muted">
        {label}
      </SkyText>
      {comparison === "different" && theirs ? (
        <SkyText size={200} color="warning" testId={`${testId}-mismatch`}>
          {admin ? ` · server ${theirs.slice(0, SHORT)}` : " · different version running"}
        </SkyText>
      ) : null}
      {showDetail && detail ? (
        <span className="block">
          <SkyText size={200} color="muted" testId={`${testId}-detail`}>
            {detail}
          </SkyText>
        </span>
      ) : null}
    </span>
  );
}
